import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, ShoppingCart, ShoppingBasket, FileCheck2, ArrowLeft } from "lucide-react";
import { basket, useBasket } from "@/lib/basket";
import { RelatedDashboards } from "@/components/Related";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { Price, CurrencySelect, useCurrency } from "@/lib/currency";
import { checkout } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { TermsCheckbox } from "@/components/site/TermsCheckbox";
import { BillingFields } from "@/components/site/BillingFields";

export default function ReportDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [agree, setAgree] = useState(false);
  const { currency, country } = useCurrency();
  const [billing, setBilling] = useState({});
  const bk = useBasket();
  const { data: r, refetch } = useQuery({ queryKey: ["report", id, !!user], queryFn: async () => (await api.get(`/reports/${id}`)).data });
  const inBasket = bk.reports.some((x) => x.id === id);
  if (!r) return <div className="py-24 text-center font-mono text-xs text-slate-500">LOADING…</div>;
  const dl = (path, name) => downloadFile(path, name).catch((e) => toast.error(errMsg(e) || "Download failed"));
  const buy = () => (user ? checkout({ type: "report", report_id: r.id, accept_terms: agree, currency, billing: { ...billing, country } }, refetch) : nav(`/login?next=/reports/${r.id}`));

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-12">
      <Link to="/reports" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> All reports</Link>
      <div className="grid lg:grid-cols-[1fr_340px] gap-10 mt-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-[#03AB9E]">{r.category}</p>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3" data-testid="report-title">{r.title}</h1>
          {r.summary && <p className="text-slate-300 mt-5">{r.summary}</p>}
          {r.description && <div className="cms-prose mt-6" dangerouslySetInnerHTML={{ __html: r.description }} />}
          <div className="mt-8 grid sm:grid-cols-3 gap-3 text-sm">
            {["Market size & forecast 2024–2030", "Demand by application & region", "Supply, trade & price outlook"].map((t) => (
              <div key={t} className="border border-slate-800 rounded-md p-4 text-slate-300">{t}</div>
            ))}
          </div>
        </div>
        <aside className="bg-[#0F172A] border border-slate-800 rounded-lg p-6 self-start lg:sticky lg:top-24" data-testid="report-buy-box">
          <Price value={r.price} big testId="report-price" />
          <p className="text-xs text-slate-500 mt-1">One-time · single-user licence · PDF</p>
          {!r.owned && <CurrencySelect />}
          {r.owned ? (
            <button onClick={() => dl(`/reports/${r.id}/download`, "report.pdf")} disabled={!r.has_full} data-testid="report-download-full-button"
              className="mt-6 w-full h-11 rounded-md bg-[#03AB9E] hover:bg-[#028F84] text-slate-950 font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              <FileCheck2 className="h-4 w-4" /> {r.has_full ? "Download full report" : "Being prepared — check back soon"}
            </button>
          ) : (
            <>
            {user && <BillingFields value={billing} onChange={setBilling} country={country} />}
            {user && <TermsCheckbox checked={agree} onChange={setAgree} />}
            <button onClick={buy} disabled={!!user && !agree} data-testid="report-buy-button" className="mt-5 disabled:opacity-50 disabled:cursor-not-allowed w-full h-11 rounded-md bg-[#F58B23] hover:bg-[#D97618] text-slate-950 font-semibold flex items-center justify-center gap-2">
              <ShoppingCart className="h-4 w-4" /> {user ? "Buy now" : "Login to buy"}
            </button>
            <button onClick={() => { basket.addReport(r.id, r.title); toast.success("Report added to basket"); }} data-testid="report-add-basket-button"
              className="mt-2 w-full h-11 rounded-md border border-slate-700 hover:border-[#F58B23] text-sm flex items-center justify-center gap-2"><ShoppingBasket className="h-4 w-4" />{inBasket ? "In basket" : "Add to basket"}</button>
            </>
          )}
          {r.has_sample && (
            <button onClick={() => dl(`/reports/${r.id}/sample`, "sample.pdf")} data-testid="report-sample-button" className="mt-3 w-full h-11 rounded-md border border-slate-700 hover:border-[#1074BA] flex items-center justify-center gap-2 text-sm">
              <Download className="h-4 w-4" /> Download free sample
            </button>
          )}
          <p className="text-[11px] text-slate-500 mt-5">Secure payment via Razorpay. Need a corporate licence? <Link to="/contact" className="text-[#03AB9E]">Contact us</Link>.</p>
        </aside>
      </div>
      <RelatedDashboards reportId={r.id} />
    </div>
  );
}
